// İş izni türleri sabiti
export const WORK_PERMIT_TYPES = [
  { id: 'sicak_calisma', label: 'Sıcak Çalışma (kesme, vb.)' },
  { id: 'kaynak', label: 'Kaynak' },
  { id: 'kapali_alan', label: 'Kapalı Alan' },
  { id: 'yuksekte_calisma', label: 'Yüksekte Çalışma' }
];

// Format date as DD/MM/YYYY
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// T.C. numarasını maskele: 41281522386 -> 412*****86
export const maskTcNo = (tc) => {
  if (!tc || tc.length < 5) return tc || '';
  return tc.substring(0, 3) + '*****' + tc.substring(tc.length - 2);
};

// Bugünün tarihini YYYY-MM-DD formatında al
export const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Check if employee has all required documents approved
export const isEmployeeFullyApproved = (employee) => {
  const docs = employee?.documents || {};
  return docs.sgk_ise_giris === 'onay' && 
         docs.isg_egitimi === 'onay' && 
         docs.saglik_raporu === 'onay';
};

// Check if company has all documents approved
export const isCompanyFullyApproved = (company) => {
  const docs = company?.documents || {};
  return docs.sgk_hizmet_listesi === 'onay' && 
         docs.isg_katip_uzman === 'onay' && 
         docs.isg_katip_hekim === 'onay';
};

// Check if permit is expired (end_date is in the past)
export const isPermitExpired = (permit) => {
  if (!permit?.end_date) return false;
  return permit.end_date.slice(0, 10) < new Date().toLocaleDateString('sv-SE');
};

// Get actual status considering expiration
export const getActualStatus = (permit) => {
  // Önce özel statüleri kontrol et
  if (['onay_bekliyor', 'red_edildi', 'isg_inceleme', 'taslak', 'eksik_hatali', 'talep_bilgisi_hatali'].includes(permit?.status)) {
    return permit.status;
  }
  if (isPermitExpired(permit)) return 'suresi_dolmus';
  return permit?.status || 'aktif';
};

// Durum label'larını al
export const getStatusLabel = (status) => {
  const labels = {
    'aktif': 'Onaylandı',
    'taslak': 'Taslak',
    'eksik_hatali': 'Eksik/Hatalı',
    'talep_bilgisi_hatali': 'Talep Bilgisi Hatalı',
    'onay_bekliyor': 'Onay Bekliyor',
    'isg_inceleme': 'İSG İncelemesinde',
    'red_edildi': 'Reddedildi',
    'geri_cekildi': 'Geri Çekildi',
    'suresi_dolmus': 'Süresi Dolmuş',
    'tamamlandi': 'Tamamlandı',
    'iptal': 'İptal'
  };
  return labels[status] || status;
};

// Belge geçerlilik bilgilerini al
export const getDocumentValidityInfo = (employee, permitEndDate) => {
  const docs = employee?.documents || {};
  const expiringDocs = [];
  
  if (!permitEndDate) return { isValid: true, expiringDocs: [] };
  
  const endDate = String(permitEndDate).slice(0, 10);
  
  // İSG Eğitimi geçerlilik kontrolü
  if (docs.isg_egitimi_validity) {
    const validityDate = String(docs.isg_egitimi_validity).slice(0, 10);
    if (validityDate < endDate) {
      expiringDocs.push({
        docName: 'İSG Eğitimi',
        expiryDate: docs.isg_egitimi_validity
      });
    }
  }
  
  // Sağlık Raporu geçerlilik kontrolü
  if (docs.saglik_raporu_validity) {
    const validityDate = String(docs.saglik_raporu_validity).slice(0, 10);
    if (validityDate < endDate) {
      expiringDocs.push({
        docName: 'Sağlık Raporu',
        expiryDate: docs.saglik_raporu_validity
      });
    }
  }
  
  // Mesleki Yeterlilik (MYK) geçerlilik kontrolü
  if (docs.mesleki_yeterlilik === 'onay' && docs.mesleki_yeterlilik_date) {
    const validityDate = String(docs.mesleki_yeterlilik_date).slice(0, 10);
    if (validityDate < endDate) {
      expiringDocs.push({
        docName: 'Mesleki Yeterlilik (MYK)',
        expiryDate: docs.mesleki_yeterlilik_date
      });
    }
  }
  
  return {
    isValid: expiringDocs.length === 0,
    expiringDocs
  };
};

// Ekipman periyodik muayene geçerlilik kontrolü
export const getEquipmentValidityInfo = (equipment, permitEndDate) => {
  const docs = equipment?.documents || {};
  
  if (!permitEndDate) return { isValid: true, expiryDate: null };
  
  const endDate = String(permitEndDate).slice(0, 10);
  
  // Periyodik muayene geçerlilik tarihi kontrolü
  const periyodikValidity = docs.periyodik_muayene_validity;
  if (periyodikValidity) {
    const validityDate = String(periyodikValidity).slice(0, 10);
    if (validityDate < endDate) {
      return {
        isValid: false,
        expiryDate: periyodikValidity
      };
    }
  } else {
    // Periyodik muayene tarihi girilmemişse geçersiz say
    return {
      isValid: false,
      expiryDate: null,
      noDate: true
    };
  }
  
  return { isValid: true, expiryDate: periyodikValidity };
};


// Audit log action labels
export const getAuditActionLabel = (action) => {
  const labels = {
    'created': 'Talep Oluşturuldu',
    'updated': 'Talep Güncellendi',
    'submitted_for_approval': '2. Onaya Gönderildi',
    'approved': 'Onaylandı',
    'rejected': 'Reddedildi',
    'finalized': 'Onaylandı ve Aktif Edildi',
    'deficiency_report_sent': 'Eksiklik Raporu Gönderildi',
    'status_changed': 'Durum Değişti',
    'withdrawn': 'Geri Çekildi',
    'marked_deficient': 'Eksik/Hatalı İşaretlendi',
    'submitted_for_review': 'İSG İncelemesine Gönderildi',
    'recalled_by_firm': 'Firma Tarafından Geri Çekildi',
    'passive': 'Pasife Alındı',
    'active': 'Aktif Edildi',
    'activated': 'Aktif Edildi',
    'until_changed': 'Pasiflik Süresi Değiştirildi'
  };
  return labels[action] || 'İşlem Yapıldı';
};

// Audit log action icons
export const getAuditActionIcon = (action) => {
  const icons = {
    'created': '📝',
    'updated': '✏️',
    'submitted_for_approval': '📤',
    'approved': '✅',
    'rejected': '❌',
    'finalized': '✓',
    'deficiency_report_sent': '📧',
    'status_changed': '🔄',
    'withdrawn': '↩️',
    'marked_deficient': '📧',
    'submitted_for_review': '📤',
    'recalled_by_firm': '↩️',
    'passive': '⏸️',
    'active': '▶️',
    'activated': '▶️',
    'until_changed': '🗓️'
  };
  return icons[action] || '📋';
};

// Kullanıcı rolü Türkçe adı
export const getRoleLabel = (role) => ({
  super_admin: 'Süper Admin', isg_expert: 'İSG Uzmanı', firma_yetkilisi: 'Firma Yetkilisi',
  '2_onayci': '2. Onaycı', user: 'Kullanıcı', viewer: 'İzleyici',
}[role] || role);

// Format audit timestamp
export const formatAuditTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};
